// src/shared/load-failure.js — say WHY a list is empty (v131.76).
//
// The replenishment lead, 16 Sep: he opened Admin > Comms > Tasks and saw no current or past
// tasks. Matt, on the same data, saw all of them. Nothing on his screen
// said anything was wrong — it said "No tasks yet."
//
// It was not lying on purpose. All three comms lists loaded like this:
//
//     var res = await api('/api/admin/tasks');
//     if (!res.ok) return;                    // 401, 403, 500, offline — same
//     ...
//     } catch (e) { /* retry on next open */ }
//
// So an expired session, a bundle that did not load, a server error and a
// genuinely empty list all render the SAME screen. The one state a person can
// act on — "sign in again" — is indistinguishable from the one they cannot.
// That is how a thirty-second problem becomes somebody walking to the owner's
// desk, and it is why nobody could tell what was wrong by looking.
//
// This turns the failure into a sentence. It is deliberately about what the
// READER should do, not about the HTTP code: "your session has expired, sign
// out and back in" is actionable; "401" is not.
//
// Shared because the three comms admin bundles (tasks, news, surveys) are
// separate esbuild outputs with byte-identical loaders — three copies of this
// wording would drift, and the next list somebody adds should be able to pick
// it up in one line.

// status 0 means the request never got an answer (offline, DNS, CORS, a
// bundle that failed to load) — distinct from a server that answered badly.
export function loadFailureMessage(status, thing, serverError) {
  const what = String(thing || 'this list');
  const code = Number(status) || 0;

  if (code === 401) {
    return 'Your session has expired, so ' + what + ' could not be loaded. Sign out and sign in again.';
  }
  if (code === 403) {
    return 'This account does not have access to ' + what + '. Ask Head Office to check your role.';
  }
  if (code === 0) {
    return 'Could not reach the server, so ' + what + ' could not be loaded. Check your connection, then refresh.';
  }
  if (code >= 500) {
    return 'The server could not return ' + what + ' (error ' + code + '). Try again in a moment; if it keeps happening, report it.';
  }
  // A 4xx we have no specific advice for. Carry the server's own words when it
  // gave any — it usually knows more than we do here.
  const detail = serverError ? ' (' + String(serverError).slice(0, 120) + ')' : '';
  return 'Could not load ' + what + detail + '. Refresh the page, and report it if it persists.';
}

// The whole loader contract in one place, so a caller cannot half-adopt it.
// Returns { ok: true, data } or { ok: false, message }. Never throws.
export async function loadList(apiFn, path, thing) {
  try {
    const res = await apiFn(path);
    if (!res || !res.ok) {
      let serverError = null;
      try { const d = await res.json(); serverError = d && d.error; } catch (e) { /* no body */ }
      return { ok: false, message: loadFailureMessage(res ? res.status : 0, thing, serverError) };
    }
    const d = await res.json();
    if (!d || d.ok === false) {
      return { ok: false, message: loadFailureMessage(res.status, thing, d && d.error) };
    }
    return { ok: true, data: d };
  } catch (e) {
    return { ok: false, message: loadFailureMessage(0, thing) };
  }
}
